/**
 * Badge-logo per marketplace usato in Ordini, Messaggi e settings.
 *
 * Audit 2026-05-28 (Davide): "voglio i loghi ufficiali in miniatura".
 * 2026-06-29 (fix loghi, Davide): "non voglio i loghi tagliati".
 *  - Amazon: era un wordmark "amazon" che eccedeva il viewBox (croppato
 *    "mazo"); ora mark QUADRATO "a"+smile arancione, intero a 12-22 px.
 *  - Brico Bravo: NON e' verde. Logo ufficiale BBO = gradiente viola→magenta
 *    (#4320A8 → #EA0299), verificato dal loro SVG. "B" su quel gradiente.
 *  - WooCommerce (2026-07-03, Davide con PNG di riferimento): SIMBOLO del
 *    rebrand Woo 2025 — cerchio viola #873EFF (campionato dal PNG) con "W"
 *    corsiva bianca. Sostituisce il path simple-icons #96588A del 07-02;
 *    lo slug legacy "bobgardens" converge sullo stesso simbolo.
 * 2026-06-29 (Task #8): estesa la copertura a TUTTI i canali che OM vede,
 * con priorita' agli ASSET UFFICIALI (simple-icons, gia' dipendenza) come
 * fa Poleepo. Dove simple-icons non ha il brand (es. Maisons du Monde,
 * Vente Unique, Metro Markets, Brico Bravo, ManoMano, Decathlon, Amazon —
 * rimossi o mai presenti) restiamo su un lettermark/SVG curato col colore
 * brand reale, riconoscibile anche a 12-22 px.
 *
 * I marketplace Mirakl arrivano con ``source="mirakl"`` e il brand vero nel
 * ``label`` (es. "Leroy Merlin", "Carrefour", "ManoMano"). Per questo il
 * resolver dei loghi ufficiali matcha PRIMA sul label normalizzato e poi
 * sullo slug del source: cosi' un thread Mirakl "Leroy Merlin" mostra il
 * logo ufficiale Leroy Merlin, non la pastiglia generica Mirakl.
 *
 * Uso interno BSCom (operatori, non clienti finali) = lecito anche con
 * marchi figurativi. Fallback pastiglia "?" per source ignote, cosi' un
 * nuovo marketplace non causa crash render finche' non lo aggiungiamo.
 */

import { useId, type ReactElement } from "react";
import {
  siCarrefour,
  siEbay,
  siLeroymerlin,
  siMediamarkt,
  siPaypal,
  siShopify,
  siWhatsapp,
  type SimpleIcon,
} from "simple-icons";

export type MarketplaceSource =
  | "amazon"
  | "ebay"
  | "shopify"
  | "woocommerce"
  | "bobgardens"
  | "privato"
  | "mirakl"
  | "metro"
  | "manomano"
  | "temu"
  | "email_imap"
  | "whatsapp";

interface MarketplaceMeta {
  label: string;
  bg: string;
}

const META: Record<MarketplaceSource, MarketplaceMeta> = {
  amazon: { label: "Amazon", bg: "#FF9900" },
  ebay: { label: "eBay", bg: "#FFFFFF" },
  shopify: { label: "Shopify", bg: "#95BF47" },
  woocommerce: { label: "WooCommerce", bg: "#873EFF" },
  // Slug legacy (thread/mock storici): converge sul logo WooCommerce.
  bobgardens: { label: "WooCommerce", bg: "#873EFF" },
  privato: { label: "Privato", bg: "#94A3B8" },
  mirakl: { label: "Mirakl", bg: "#7C5A8A" },
  metro: { label: "Metro", bg: "#003D7A" },
  manomano: { label: "ManoMano", bg: "#FA9020" },
  temu: { label: "Temu", bg: "#FB7701" },
  email_imap: { label: "Email", bg: "#5B6B82" },
  whatsapp: { label: "WhatsApp", bg: "#25D366" },
};

const UNKNOWN_BG = "#807A70";
const UNKNOWN_LABEL = "Marketplace";

/**
 * Restituisce la label leggibile di un canale/marketplace a partire dal suo
 * slug (es. "whatsapp" → "WhatsApp", "email_imap" → "Email"). Usato anche da
 * moduli non-rendering (es. notifiche) che non montano il componente React.
 * Fallback al raw se lo slug non è mappato.
 */
export function sourceLabel(source: string): string {
  const key = normalize(source);
  return META[key as MarketplaceSource]?.label ?? source;
}

/** Normalizza qualunque input (slug snake_case, label PascalCase, etc)
 *  alla chiave di lookup. ``Amazon`` -> ``amazon``; spazi e ``-``
 *  collassati a ``_``. */
function normalize(source: string): string {
  return (source || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

interface MarketplaceLogoProps {
  /** Slug del marketplace (es. ``amazon``, ``mirakl``) o label
   *  capitalizzata (es. ``Amazon``, ``Privato``). Normalizzato internamente. */
  source: string;
  /** Lato del badge circolare in pixel. Default 18. */
  size?: number;
  /** Se true mostra la label accanto al cerchio (es. " Amazon"). */
  showLabel?: boolean;
  /** Override del tooltip / label visibile (utile per source ambigui:
   *  es. Mirakl con label "Maisons du Monde"). */
  label?: string;
  /** Classi extra per il wrapper esterno (per spacing in-row). */
  className?: string;
}

// ────────────────────────────────────────────────────────────────────
// Loghi UFFICIALI da simple-icons (path monocromatico + colore brand).
//
// Inclusi SOLO i brand il cui export simple-icons corrisponde davvero al
// marketplace giusto. ATTENZIONE: ``siMetro`` in simple-icons NON e' Metro
// Markets (e' un dev-tool React Native) → NON va usato per il marketplace
// Metro; resta il lettermark curato. ``siAmazon``, ``siManomano``,
// ``siDecathlon`` non esistono nel pacchetto installato → lettermark curato.
//
// Le chiavi sono token NORMALIZZATI (snake_case) confrontati sia col label
// (Mirakl espone qui il brand vero, es. "leroy_merlin") sia con lo slug.
// ────────────────────────────────────────────────────────────────────
const SIMPLE_ICONS: Record<string, SimpleIcon> = {
  ebay: siEbay,
  shopify: siShopify,
  carrefour: siCarrefour,
  mediamarkt: siMediamarkt,
  media_markt: siMediamarkt,
  leroy_merlin: siLeroymerlin,
  leroymerlin: siLeroymerlin,
  whatsapp: siWhatsapp,
  // Pagina Saldi (28/10/2026): il conto PayPal di Kery.
  paypal: siPaypal,
};

/**
 * Risolve il logo ufficiale (simple-icons) provando le chiavi nell'ordine
 * dato. Per ogni chiave tenta:
 *  1. match esatto sul token normalizzato (``leroy_merlin``);
 *  2. match "compatto" senza separatori (``leroymerlin``), cosi' label
 *     scritte in modi diversi ("Leroy Merlin" / "LeroyMerlin") convergono.
 * Ritorna ``null`` se nessuna chiave corrisponde a un asset ufficiale.
 */
function resolveSimpleIcon(keys: string[]): SimpleIcon | null {
  for (const raw of keys) {
    const k = normalize(raw);
    if (!k) continue;
    if (SIMPLE_ICONS[k]) return SIMPLE_ICONS[k];
    const compact = k.replace(/_/g, "");
    if (SIMPLE_ICONS[compact]) return SIMPLE_ICONS[compact];
  }
  return null;
}

function SimpleIconBadge({ icon, size }: { icon: SimpleIcon; size: number }): ReactElement {
  // Path brand 24x24 su sfondo bianco arrotondato, con un filo di padding
  // (viewBox allargato) per leggibilita' anche in miniatura.
  return (
    <svg width={size} height={size} viewBox="-4 -4 32 32" aria-hidden="true">
      <rect x="-4" y="-4" width="32" height="32" rx="7" fill="#FFFFFF" />
      <path d={icon.path} fill={`#${icon.hex}`} />
    </svg>
  );
}

// ────────────────────────────────────────────────────────────────────
// Lettermark curati per i brand SENZA asset simple-icons. Colore brand
// reale + iniziale/wordmark stilizzato. Pensati per 12-22 px: conta il
// colore + la forma, non la fedelta' al pixel.
// ────────────────────────────────────────────────────────────────────

/** Pastiglia con iniziale: fondo brand + glifo (lettera/simbolo) centrato.
 *  Base condivisa dei lettermark per coerenza ottica. */
function Lettermark({
  size,
  bg,
  fg,
  glyph,
  fontSize = 13,
  fontWeight = 800,
}: {
  size: number;
  bg: string;
  fg: string;
  glyph: string;
  fontSize?: number;
  fontWeight?: number;
}): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="5" fill={bg} />
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="Nunito, Arial, sans-serif"
        fontWeight={fontWeight}
        fontSize={fontSize}
        fill={fg}
      >
        {glyph}
      </text>
    </svg>
  );
}

function AmazonIcon({ size }: { size: number }): ReactElement {
  // Mark QUADRATO Amazon: lettera "a" bianca + "smile" arancione (la freccia
  // a→z col sorriso). NIENTE wordmark "amazon" (eccedeva il viewBox 24x24 →
  // veniva tagliato "mazo"). simple-icons NON espone piu' siAmazon (Amazon lo
  // ha rimosso), quindi ricostruiamo un mark quadrato fedele e centrato, con
  // padding interno: resta intero e nitido a 12-22 px, mai croppato.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="5" fill="#0F1B2D" />
      <text
        x="12"
        y="11"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="700"
        fontSize="13"
        fill="#FFFFFF"
      >
        a
      </text>
      {/* Smile: arco arancione sotto la "a", con padding ai lati (x 5→19). */}
      <path
        d="M5 16 C 8.6 18.7, 15.4 18.7, 19 16"
        stroke="#FF9900"
        strokeWidth="1.9"
        strokeLinecap="round"
        fill="none"
      />
      {/* Punta freccia all'estremita' destra dello smile. */}
      <path d="M17.6 14.7 L 19.6 16.1 L 17.3 16.9 Z" fill="#FF9900" />
    </svg>
  );
}

function ManoManoIcon({ size }: { size: number }): ReactElement {
  // Logo UFFICIALE ManoMano (Davide 2026-07-06, da brand asset 2018+):
  // esagono arrotondato con gradiente teal (verde-acqua → teal scuro) e
  // "M" bianca. Il vecchio lettermark arancione #FA9020 era un colore
  // inventato, mai stato del brand. useId → gradiente unico per istanza
  // (la lista rende N loghi: niente id duplicati nel DOM).
  const gradientId = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3BEFD0" />
          <stop offset="1" stopColor="#14ADA4" />
        </linearGradient>
      </defs>
      {/* Esagono coi vertici a sx/dx (come l'ufficiale); stroke round
          sullo stesso gradiente = angoli arrotondati senza path curve. */}
      <path
        d="M20.8 12 16.4 4.6H7.6L3.2 12l4.4 7.4h8.8z"
        fill={`url(#${gradientId})`}
        stroke={`url(#${gradientId})`}
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path
        d="M7.6 16.4V8.9l2.2-1.1 2.2 3.5 2.2-3.5 2.2 1.1v7.5h-2.4v-3.7L12 15.2l-2-2.5v3.7z"
        fill="#FFFFFF"
      />
    </svg>
  );
}

function MetroIcon({ size }: { size: number }): ReactElement {
  // Metro Markets: blu brand + "M" gialla (siMetro e' un altro brand).
  return (
    <Lettermark size={size} bg="#003D7A" fg="#FFCB04" glyph="M" fontSize={15} fontWeight={900} />
  );
}

function MaisonsDuMondeIcon({ size }: { size: number }): ReactElement {
  // Maisons du Monde: nero brand + "M" bianca (no asset simple-icons).
  return (
    <Lettermark size={size} bg="#1A1A1A" fg="#FFFFFF" glyph="M" fontSize={15} fontWeight={700} />
  );
}

function VenteUniqueIcon({ size }: { size: number }): ReactElement {
  // Vente-Unique: rosso brand + "V" bianca (no asset simple-icons).
  return (
    <Lettermark size={size} bg="#E2231A" fg="#FFFFFF" glyph="V" fontSize={15} fontWeight={800} />
  );
}

function BricoBravoIcon({ size }: { size: number }): ReactElement {
  // Brico Bravo (verificato dal logo ufficiale BBO su bricobravo.com): il
  // brand NON e' verde (errore precedente) ma un GRADIENTE viola→magenta
  // (#4320A8 in basso → #EA0299 in alto, mark "BBO"). Rendo una "B" bianca su
  // quel gradiente. id univoco per istanza, cosi' piu' loghi in pagina non
  // condividono/sovrascrivono lo stesso <linearGradient>.
  const gid = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="#4320A8" />
          <stop offset="1" stopColor="#EA0299" />
        </linearGradient>
      </defs>
      <rect width="24" height="24" rx="5" fill={`url(#${gid})`} />
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="Nunito, Arial, sans-serif"
        fontWeight={800}
        fontSize={15}
        fill="#FFFFFF"
      >
        B
      </text>
    </svg>
  );
}

function TemuIcon({ size }: { size: number }): ReactElement {
  // Temu (ordini TEMU_IT dal 22/10): arancio brand + "T" bianca (no asset simple-icons).
  return (
    <Lettermark size={size} bg="#FB7701" fg="#FFFFFF" glyph="T" fontSize={15} fontWeight={800} />
  );
}

function DecathlonIcon({ size }: { size: number }): ReactElement {
  // Decathlon: blu brand + "D" bianca (no asset simple-icons installato).
  return (
    <Lettermark size={size} bg="#0082C3" fg="#FFFFFF" glyph="D" fontSize={15} fontWeight={800} />
  );
}

function WooCommerceIcon({ size }: { size: number }): ReactElement {
  // Simbolo WooCommerce (rebrand 2025, PNG di riferimento di Davide
  // 2026-07-03): cerchio viola #873EFF pieno con "W" corsiva bianca a
  // estremita' arrotondate. Disegnata come stroke path (non font) per
  // essere identica ovunque e nitida a 12-22 px.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="12" fill="#873EFF" />
      <path
        d="M6.4 9.6 L 8.9 15.4 L 11.3 9.9 L 13.1 15.4 L 17.6 8.6"
        stroke="#FFFFFF"
        strokeWidth="2.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function MiraklIcon({ size }: { size: number }): ReactElement {
  // Mirakl "puro" (label generica, nessun brand specifico): viola + "M".
  return (
    <Lettermark size={size} bg="#7C5A8A" fg="#FFFFFF" glyph="M" fontSize={15} fontWeight={900} />
  );
}

function EmailIcon({ size }: { size: number }): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#5B6B82" />
      <text
        x="12"
        y="17"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="700"
        fontSize="14"
        fill="#FFFFFF"
      >
        @
      </text>
    </svg>
  );
}

function PrivatoIcon({ size }: { size: number }): ReactElement {
  // Silhouette persona generica grigia (uso interno per ordini "privato"
  // diretti, non da marketplace).
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#94A3B8" />
      <circle cx="12" cy="9.5" r="3" fill="#FFFFFF" />
      <path d="M5.5 19 C 6.5 15.5 9 14 12 14 C 15 14 17.5 15.5 18.5 19 Z" fill="#FFFFFF" />
    </svg>
  );
}

function UnknownIcon({ size }: { size: number }): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill={UNKNOWN_BG} />
      <text
        x="12"
        y="17"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="700"
        fontSize="14"
        fill="#FFFFFF"
      >
        ?
      </text>
    </svg>
  );
}

/**
 * Lettermark curati per brand SENZA asset simple-icons, indicizzati per
 * token normalizzato (label Mirakl o slug). Provati DOPO i loghi ufficiali.
 */
const CURATED_BRANDS: Record<string, (size: number) => ReactElement> = {
  amazon: (s) => <AmazonIcon size={s} />,
  manomano: (s) => <ManoManoIcon size={s} />,
  metro: (s) => <MetroIcon size={s} />,
  metro_markets: (s) => <MetroIcon size={s} />,
  maisons_du_monde: (s) => <MaisonsDuMondeIcon size={s} />,
  maison_du_monde: (s) => <MaisonsDuMondeIcon size={s} />,
  vente_unique: (s) => <VenteUniqueIcon size={s} />,
  venteunique: (s) => <VenteUniqueIcon size={s} />,
  brico_bravo: (s) => <BricoBravoIcon size={s} />,
  bricobravo: (s) => <BricoBravoIcon size={s} />,
  decathlon: (s) => <DecathlonIcon size={s} />,
  temu: (s) => <TemuIcon size={s} />,
  // Simbolo WooCommerce (ordini del sito); slug legacy convergono qui.
  woocommerce: (s) => <WooCommerceIcon size={s} />,
  bobgardens: (s) => <WooCommerceIcon size={s} />,
  bob_gardens: (s) => <WooCommerceIcon size={s} />,
};

function resolveCuratedBrand(keys: string[]): ((size: number) => ReactElement) | null {
  for (const raw of keys) {
    const k = normalize(raw);
    if (!k) continue;
    if (CURATED_BRANDS[k]) return CURATED_BRANDS[k];
    const compact = k.replace(/_/g, "");
    if (CURATED_BRANDS[compact]) return CURATED_BRANDS[compact];
  }
  return null;
}

/** Fallback per i source "canonici" (slug noto) quando non c'e' ne' asset
 *  ufficiale ne' brand curato match-ato via label.
 *  ponytail: solo gli slug assenti da SIMPLE_ICONS e CURATED_BRANDS arrivano
 *  qui — gli altri sono gia' stati risolti prima (vedi MarketplaceLogo). */
function LogoIcon({ source, size }: { source: string; size: number }): ReactElement {
  switch (source) {
    case "mirakl":
      return <MiraklIcon size={size} />;
    case "email_imap":
      return <EmailIcon size={size} />;
    case "privato":
      return <PrivatoIcon size={size} />;
    default:
      return <UnknownIcon size={size} />;
  }
}

export function MarketplaceLogo({
  source,
  size = 18,
  showLabel = false,
  label,
  className,
}: MarketplaceLogoProps): ReactElement {
  const key = normalize(source);
  const meta = META[key as MarketplaceSource];
  const displayLabel = label || meta?.label || UNKNOWN_LABEL;
  // Ordine di risoluzione del logo (come Poleepo: prima l'asset ufficiale):
  //  1. simple-icons UFFICIALE — match su label (Mirakl espone qui il brand
  //     vero, es. "Leroy Merlin"/"Carrefour") poi su slug;
  //  2. lettermark BRAND curato (Amazon, ManoMano, Maisons du Monde, Vente
  //     Unique, Metro Markets, Brico Bravo, Decathlon) match su label/slug;
  //  3. LogoIcon per slug canonico (mirakl/email_imap/privato/…);
  //  4. UnknownIcon ("?").
  const lookupKeys = [label ?? "", key];
  const simpleIcon = resolveSimpleIcon(lookupKeys);
  const curated = simpleIcon ? null : resolveCuratedBrand(lookupKeys);
  return (
    <span
      className={["inline-flex items-center gap-1.5", className ?? ""].join(" ").trim()}
      title={displayLabel}
      aria-label={displayLabel}
      data-testid="marketplace-logo"
      data-source={key}
    >
      {simpleIcon ? (
        <SimpleIconBadge icon={simpleIcon} size={size} />
      ) : curated ? (
        curated(size)
      ) : (
        <LogoIcon source={key} size={size} />
      )}
      {showLabel && <span className="text-[11.5px] text-ink-2">{displayLabel}</span>}
    </span>
  );
}
